import { memo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import { WORK_ORDER_DATE_FORMAT } from '../../../WorkOrders/WorkOrderDatePicker';
import AssetManagerService, { getAssetManagerErrorMessage } from '../../../../../services/AssetManager/assetManager.service';
import { assetManagerServiceMessages } from '../../../../../services/AssetManager/assetManager.constants';
import Notification from '../../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../../enums/notificationTypes';
import type { AssetKey, AssetWorkOrderSummary } from '../../../../../types/InfrastructureManagerTypes';
import styles from './AssetListPanel.module.scss';

interface AssetWorkOrderDetailsProps extends AssetKey {
  id: string;
}

const NO_WORK_ORDERS: AssetWorkOrderSummary[] = [];
const SCROLL_AFTER_WORK_ORDERS = 2;

const formatDate = (value?: string) => (value ? dayjs(value).format(WORK_ORDER_DATE_FORMAT) : undefined);

// Mounted only while its row is expanded and rendered, so the details query is released as soon as either stops.
const AssetWorkOrderDetails = ({ id, layerId, keyValue }: AssetWorkOrderDetailsProps) => {
  const { getAssetDetails } = AssetManagerService();
  const { data: details, isLoading: isLoadingDetails, error } = useQuery(getAssetDetails({ layerId, keyValue }));

  useEffect(() => {
    if (!error) return;
    Notification({
      // Keyed so several expanded rows failing together show one toast.
      key: assetManagerServiceMessages.ASSETS.GET_DETAILS_ERROR,
      message: assetManagerServiceMessages.ASSETS.GET_DETAILS_ERROR,
      description: getAssetManagerErrorMessage(error),
      type: NotificationTypes.ERROR,
    });
  }, [error]);

  if (isLoadingDetails) {
    return (
      <div id={id} className={styles.workOrderLoading}>
        <Spin size="small" />
      </div>
    );
  }

  const items = details?.workOrders.items ?? NO_WORK_ORDERS;

  return (
    <article id={id} className={styles.workOrderBody}>
      <p className={styles.workOrderSectionLabel}>Work Orders:</p>

      {items.length ? (
        <div className={items.length > SCROLL_AFTER_WORK_ORDERS ? styles.workOrderItemsScroll : undefined}>
          {items.map((workOrder) => (
            <section key={workOrder.id} className={styles.workOrderItem}>
              <div className={styles.workOrderSummary}>
                <p className={styles.workOrderName}>{workOrder.title}</p>
                {workOrder.description ? <p className={styles.workOrderDescription}>{workOrder.description}</p> : null}
              </div>
              <dl className={styles.workOrderFields}>
                <div className={styles.workOrderField}>
                  <dt className={styles.workOrderFieldLabel}>Submitted On</dt>
                  <dd className={styles.workOrderFieldValue}>{formatDate(workOrder.submittedOn)}</dd>
                </div>
                <div className={styles.workOrderField}>
                  <dt className={styles.workOrderFieldLabel}>Last Edited By</dt>
                  <dd className={styles.workOrderFieldValue}>{workOrder.lastEditedBy}</dd>
                </div>
                <div className={styles.workOrderField}>
                  <dt className={styles.workOrderFieldLabel}>Last Edited</dt>
                  <dd className={styles.workOrderFieldValue}>{formatDate(workOrder.lastEditedOn)}</dd>
                </div>
              </dl>
            </section>
          ))}
        </div>
      ) : (
        <div className={styles.workOrderEmpty} />
      )}
    </article>
  );
};

export default memo(AssetWorkOrderDetails);
